import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import GroupManagement from '../components/settings/GroupManagement';
import TagManagement from '../components/settings/TagManagement';
import TypeManagement from '../components/settings/TypeManagement';
import CategoryManagement from '../components/settings/CategoryManagement';
import SourceManagement from '../components/settings/SourceManagement';
import AgentManagement from '../components/settings/UserManagement';
import AuditLogs from '../components/settings/AuditLogs';
import BillingManagement from '../components/settings/billing/BillingManagement';
// import PushNotificationSettings from '../components/settings/PushNotificationSettings';
import FormTemplateManagement from '../components/settings/FormTemplateManagement';
import OmniChannelSettings from '../components/settings/OmniChannelSettings';
// import BotConfiguration from '../components/settings/BotConfiguration';
import EditFormTemplatePage from './EditFormTemplatePage';
import { TemplateEditorProvider } from '../contexts/TemplateEditorContext';

import DepartmentManagement from '../components/settings/DepartmentManagement';
import CustomFieldsManager from '../components/customer360/CustomFieldsManager';
import TicketTitleSuggestionsSettings from '../components/settings/TicketTitleSuggestionsSettings';
import ApiKeyManagement from '../components/settings/ApiKeyManagement';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import AccessDenied from '../components/common/AccessDenied';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthContext } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const SETTINGS_PREFERENCE_KEY = 'lastSelectedSettings';

const getSettingsSections = (t: any) => [
  // Work settings
  {
    id: 'sources',
    label: t('settings.sources'),
    component: SourceManagement,
    roles: ['admin'],
    category: 'work'
  },
  {
    id: 'categories',
    label: t('settings.categories'),
    component: CategoryManagement,
    roles: ['admin'],
    category: 'work'
  },
  {
    id: 'types',
    label: t('settings.types'),
    component: TypeManagement,
    roles: ['admin'],
    category: 'work'
  },
  {
    id: 'tags',
    label: t('settings.tags'),
    component: TagManagement,
    roles: ['admin'],
    category: 'work'
  },
  {
    id: 'templates',
    label: t('settings.templates'),
    component: FormTemplateManagement,
    roles: ['admin'],
    category: 'work'
  },
  {
    id: 'title-suggestions',
    label: t('settings.ticketTitleSuggestions.title', 'Title Suggestions'),
    component: TicketTitleSuggestionsSettings,
    roles: ['admin'],
    category: 'work'
  },

  // People settings
  {
    id: 'agents',
    label: t('settings.agents'),
    component: AgentManagement,
    roles: ['admin'],
    category: 'people'
  },
  {
    id: 'groups',
    label: t('settings.groups'),
    component: GroupManagement,
    roles: ['admin'],
    category: 'people'
  },
  {
    id: 'departments',
    label: 'Departments',
    component: DepartmentManagement,
    roles: ['admin'],
    category: 'people'
  },
  {
    id: 'custom-fields',
    label: 'Fields',
    component: CustomFieldsManager,
    roles: ['admin'],
    category: 'people'
  },

  // System settings
  // { id: 'integrations', label: t('settings.integrations'), component: PushNotificationSettings, roles: ['admin'], category: 'system' },
  {
    id: 'omnichannel',
    label: t('atrium.shortTitle'),
    component: OmniChannelSettings,
    roles: ['admin'],
    category: 'system'
  },
  // { id: 'bot', label: t('settings.botConfiguration'), component: BotConfiguration, roles: ['admin'], category: 'system' },
  {
    id: 'billing',
    label: t('settings.billing'),
    component: BillingManagement,
    roles: ['admin'],
    requireSuperAdmin: true,
    category: 'system'
  },
  {
    id: 'audit-logs',
    label: t('settings.auditLogs'),
    component: AuditLogs,
    roles: ['admin'],
    category: 'system'
  },
  {
    id: 'api-keys',
    label: 'Keys',
    component: ApiKeyManagement,
    roles: ['admin'],
    category: 'system'
  }
];

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { user: contextUser } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const currentPath = location.pathname.replace('/settings/', '');

  const user = authUser || contextUser;
  const isSuperAdmin = !!user?.isPlatformAdmin;

  // Get settings sections with translations
  const settingsSections = getSettingsSections(t);

  // Filter sections based on user role and super admin status
  const allowedSections = settingsSections.filter(
    (section) =>
      section.roles.includes(user?.role || '') && (!section.requireSuperAdmin || isSuperAdmin)
  );

  const hasAccess = user?.role === 'admin';

  // Persist the selected settings section
  useEffect(() => {
    const primarySection = currentPath.split('/')[0] || '';
    const pathMatchesAllowedSection = allowedSections.some(
      (section) =>
        currentPath === section.id || currentPath.startsWith(`${section.id}/`)
    );

    // If currentPath is empty or just 'settings', redirect to last selected or default
    if (!currentPath) {
      const lastSelected = localStorage.getItem(SETTINGS_PREFERENCE_KEY);
      const validSection = allowedSections.find((s) => s.id === lastSelected);
      const targetSection = validSection ? lastSelected : allowedSections[0]?.id || 'sources';
      navigate(`/settings/${targetSection}`, { replace: true });
    } else if (currentPath !== 'sources' && !pathMatchesAllowedSection) {
      // If current path is not allowed, save preference and redirect
      const lastSelected = localStorage.getItem(SETTINGS_PREFERENCE_KEY);
      const validSection = allowedSections.find((s) => s.id === lastSelected);
      const targetSection = validSection ? lastSelected : allowedSections[0]?.id || 'sources';
      navigate(`/settings/${targetSection}`, { replace: true });
    } else if (primarySection) {
      // Save the current selection (base section) to localStorage
      localStorage.setItem(SETTINGS_PREFERENCE_KEY, primarySection);
    }
  }, [currentPath, allowedSections, navigate]);

  if (!hasAccess) {
    return <AccessDenied message={t('settings.accessDenied')} />;
  }

  // If current path is not allowed or requires super admin access, show access denied
  if (currentPath === 'billing' && !isSuperAdmin) {
    return <AccessDenied message={t('settings.billingAccessDenied')} />;
  } else if (
    currentPath !== 'sources' &&
    !allowedSections.find((s) => currentPath === s.id || currentPath.startsWith(s.id + '/'))
  ) {
    console.log('access denied', currentPath);
    return <Navigate to="/settings/sources" replace />;
  }

  return (
    <DashboardLayout>
      <TemplateEditorProvider>
        <div
          className={`flex-1 mx-auto p-6 sm:px-6 lg:px-8 ${theme === 'dark' ? 'bg-gray-900' : ''}`}
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Sidebar */}
            <aside className="md:w-48">
              <nav
                className={`sticky top-6 space-y-3 p-4 rounded-lg shadow-sm ${
                  theme === 'dark' ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white'
                }`}
              >
                {/* Work Settings */}
                <div>
                  <h3
                    className={`px-3 text-xs font-semibold uppercase tracking-wider mb-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.categories.work', 'Work')}
                  </h3>
                  <div className="space-y-1">
                    {allowedSections
                      .filter((section) => section.category === 'work')
                      .map((section: any) => (
                        <Link
                          key={section.id}
                          to={`/settings/${section.id}`}
                          className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-md
                          ${
                            currentPath === section.id || currentPath.startsWith(section.id + '/')
                              ? theme === 'dark'
                                ? 'bg-indigo-900/50 text-indigo-300'
                                : 'bg-indigo-50 text-indigo-700'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                                : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'
                          }
                        `}
                        >
                          {section.label}
                        </Link>
                      ))}
                  </div>
                </div>

                {/* People Settings */}
                <div>
                  <h3
                    className={`px-3 text-xs font-semibold uppercase tracking-wider mb-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.categories.people', 'People')}
                  </h3>
                  <div className="space-y-1">
                    {allowedSections
                      .filter((section) => section.category === 'people')
                      .map((section: any) => (
                        <Link
                          key={section.id}
                          to={`/settings/${section.id}`}
                          className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-md
                          ${
                            currentPath === section.id || currentPath.startsWith(section.id + '/')
                              ? theme === 'dark'
                                ? 'bg-indigo-900/50 text-indigo-300'
                                : 'bg-indigo-50 text-indigo-700'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                                : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'
                          }
                        `}
                        >
                          {section.label}
                        </Link>
                      ))}
                  </div>
                </div>

                {/* System Settings */}
                <div>
                  <h3
                    className={`px-3 text-xs font-semibold uppercase tracking-wider mb-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.categories.system', 'System')}
                  </h3>
                  <div className="space-y-1">
                    {allowedSections
                      .filter((section) => section.category === 'system')
                      .map((section: any) => (
                        <Link
                          key={section.id}
                          to={`/settings/${section.id}`}
                          className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-md
                          ${
                            currentPath === section.id || currentPath.startsWith(section.id + '/')
                              ? theme === 'dark'
                                ? 'bg-indigo-900/50 text-indigo-300'
                                : 'bg-indigo-50 text-indigo-700'
                              : theme === 'dark'
                                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                                : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'
                          }
                        `}
                        >
                          {section.label}
                        </Link>
                      ))}
                  </div>
                </div>
              </nav>
            </aside>

            {/* Main content */}
            <main className="flex-1">
              <div
                className={`shadow rounded-lg p-6 ${
                  theme === 'dark' ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white'
                }`}
              >
                <Routes>
                  {allowedSections.map((section: any) => (
                    <Route key={section.id} path={section.id} element={<section.component />} />
                  ))}
                  <Route path="templates/edit/:id" element={<EditFormTemplatePage />} />
                  <Route index element={<Navigate to={allowedSections[0].id} replace />} />
                </Routes>
              </div>
            </main>
          </div>
        </div>
      </TemplateEditorProvider>
    </DashboardLayout>
  );
}
